import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

import patientService from "../../services/patientService";

const PatientListPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [patients, setPatients] = useState([]);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // Search filters state
  const [filters, setFilters] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    city: "",
    state: "",
    country: "",
    zipCode: "",
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      setError(null);
      setSearchPerformed(true);

      console.log("🔍 Searching patients with filters:", filters);

      const response = await patientService.searchPatients(filters);

      if (response.success) {
        console.log("✅ Search results:", response.data);
        setPatients(response.data || []);
      } else {
        setError(response.message || "Failed to search patients");
        setPatients([]);
      }
    } catch (err) {
      console.error("❌ Error searching patients:", err);
      setError("Failed to search patients: " + err.message);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleListAll = async () => {
    try {
      setLoading(true);
      setError(null);
      setSearchPerformed(true);

      console.log("📋 Loading all patients...");

      const response = await patientService.getAllPatients();

      if (response.success) {
        console.log("✅ All patients loaded:", response.data);
        setPatients(response.data || []);
      } else {
        setError(response.message || "Failed to load patients");
        setPatients([]);
      }
    } catch (err) {
      console.error("❌ Error loading patients:", err);
      setError("Failed to load patients: " + err.message);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      city: "",
      state: "",
      country: "",
      zipCode: "",
    });
    setPatients([]);
    setSearchPerformed(false);
    setError(null);
  };

  const getInitials = (patient) => {
    const first = patient.firstName?.[0] || "";
    const last = patient.lastName?.[0] || "";
    return (first + last).toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      "#1976d2",
      "#388e3c",
      "#d32f2f",
      "#7b1fa2",
      "#f57c00",
      "#0288d1",
      "#c2185b",
      "#5d4037",
    ];
    const index = name?.length || 0;
    return colors[index % colors.length];
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Patient Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => navigate("/patients/new")}
        >
          Register New Patient
        </Button>
      </Box>

      {/* Search Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
          Search Patients
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={filters.firstName}
              onChange={handleFilterChange}
              placeholder="Enter first name"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={filters.lastName}
              onChange={handleFilterChange}
              placeholder="Enter last name"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Date of Birth"
              name="dateOfBirth"
              type="date"
              value={filters.dateOfBirth}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="City"
              name="city"
              value={filters.city}
              onChange={handleFilterChange}
              placeholder="Enter city"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="State"
              name="state"
              value={filters.state}
              onChange={handleFilterChange}
              placeholder="Enter state"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="ZIP Code"
              name="zipCode"
              value={filters.zipCode}
              onChange={handleFilterChange}
              placeholder="Enter ZIP code"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Country"
              name="country"
              value={filters.country}
              onChange={handleFilterChange}
              placeholder="Enter country"
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={3}
            sx={{ display: "flex", alignItems: "flex-end" }}
          >
            {/* Empty space for alignment */}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            disabled={loading}
          >
            Clear Filters
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={loading}
          >
            Search
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<ListAltIcon />}
            onClick={handleListAll}
            disabled={loading}
          >
            List All Patients
          </Button>
        </Box>
      </Paper>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Results */}
      {!loading && searchPerformed && (
        <>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              {patients.length === 0
                ? "No patients found"
                : `Found ${patients.length} patient${
                    patients.length !== 1 ? "s" : ""
                  }`}
            </Typography>
          </Box>

          {patients.length === 0 ? (
            <Paper elevation={1} sx={{ p: 4, textAlign: "center" }}>
              <PersonIcon
                sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary">
                No patients match your search criteria
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your filters or click "List All Patients" to see
                all patients
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {patients.map((patient) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={patient.id}>
                  <Card
                    elevation={2}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Avatar and Name */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: getAvatarColor(patient.firstName),
                            width: 56,
                            height: 56,
                            mr: 2,
                            fontSize: "1.25rem",
                          }}
                        >
                          {getInitials(patient)}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {patient.fullName ||
                              `${patient.firstName} ${patient.lastName}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ID: {patient.id}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Patient Details */}
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          📧 {patient.email || "N/A"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          📞 {patient.phoneNumber || "N/A"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          📍{" "}
                          {patient.city && patient.state
                            ? `${patient.city}, ${patient.state}`
                            : "Location N/A"}
                        </Typography>
                        {patient.age && (
                          <Typography variant="body2" color="text.secondary">
                            🎂 Age: {patient.age}
                          </Typography>
                        )}
                      </Box>

                      {/* Status Chips */}
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {patient.ckdStage && (
                          <Chip
                            label={`Stage ${patient.ckdStage}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        {patient.profileComplete && (
                          <Chip
                            label="Profile Complete"
                            size="small"
                            color="success"
                          />
                        )}
                        {patient.hasRequiredConsents && (
                          <Chip label="Consented" size="small" color="info" />
                        )}
                      </Box>
                    </CardContent>

                    {/* Action Buttons */}
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => navigate(`/patients/${patient.id}`)}
                        fullWidth
                        variant="outlined"
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => navigate(`/patients/edit/${patient.id}`)}
                        fullWidth
                        variant="contained"
                      >
                        Edit
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Initial State - No Search Performed */}
      {!loading && !searchPerformed && (
        <Paper elevation={1} sx={{ p: 6, textAlign: "center" }}>
          <SearchIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Search for Patients
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Use the search filters above to find specific patients, or click
            "List All Patients" to see everyone.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              Search with Current Filters
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<ListAltIcon />}
              onClick={handleListAll}
            >
              List All Patients
            </Button>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default PatientListPage;
